import { clsx, type ClassValue } from "clsx";
import { twMerge } from "tailwind-merge";
import { precoDeReferencia } from "@workspace/text";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export const formatWhatsApp = (value: string) => {
  if (!value) return "";
  let digits = value.replace(/\D/g, "");
  if (digits.length === 0) return "";
  if (digits.startsWith("55") && digits.length >= 12) {
    digits = digits.slice(2);
  }
  if (digits.length <= 2) return `(${digits}`;
  if (digits.length <= 6) return `(${digits.slice(0, 2)}) ${digits.slice(2)}`;
  if (digits.length <= 10) return `(${digits.slice(0, 2)}) ${digits.slice(2, 6)}-${digits.slice(6)}`;
  if (digits.length === 11) return `(${digits.slice(0, 2)}) ${digits[2]} ${digits.slice(3, 7)}-${digits.slice(7)}`;
  return digits;
};

export const stripWhatsApp = (value: string) => {
  return value.replace(/\D/g, "");
};

// Telefone pro link api.whatsapp.com/wa.me (exige código do país). Cliente
// salvo pelo PDV antes de jul/2026 pode estar no banco SEM o 55 — sem isso o
// WhatsApp lê o DDD como código de país e não acha o número. BR local:
// 10 dígitos (fixo) ou 11 com 9 na 3ª posição (celular); o resto (12-13
// começando com 55, internacionais com outro DDI) passa como está.
export const waPhone = (value: string | null | undefined) => {
  const digits = (value || "").replace(/\D/g, "");
  if (digits.length === 10 || (digits.length === 11 && digits[2] === "9")) {
    return `55${digits}`;
  }
  return digits;
};

// Cliente sem WhatsApp de verdade (💡 #87): o PDV grava `__avulso__<loja>` (balcão) ou `__avulso__<loja>_<ts>` (cliente
// salvo sem número) no lugar do número — a regra de Clientes e Pontos —, e menos de 10 dígitos não é telefone.
export const temWhatsApp = (value: string | null | undefined) => {
  const v = String(value ?? "");
  return !v.startsWith("__avulso__") && v.replace(/\D/g, "").length >= 10;
};

// Telefone do link, ou "" quando o cliente não tem WhatsApp de verdade.
export const telefoneDoLink = (value: string | null | undefined) => (temWhatsApp(value) ? waPhone(value) : "");

// Link do WhatsApp com a mensagem pronta. Sem telefone abre SEM destinatário: a lojista escolhe o contato lá.
export const linkDoWhatsApp = (telefone: string, texto: string) =>
  `https://api.whatsapp.com/send?${telefone ? `phone=${telefone}&` : ""}text=${encodeURIComponent(texto)}`;

export const formatCurrency = (value: number) => {
  return new Intl.NumberFormat("pt-BR", {
    style: "currency",
    currency: "BRL",
  }).format(value);
};

// Identificador EXIBIDO do pedido conforme o estilo da loja: "numero" =
// sequencial do dia (0012, default); "codigo" = fim do UUID (A1B2, estilo
// antigo). Pedido sem número (anterior à numeração) cai no código.
// Aniversário que veio só com dia e mês (importação de clientes): o ano gravado é o genérico, 1900 (o 29/02 vai com
// 1904, que é bissexto). Nesses a tela mostra só DD/MM (Pedro, 03/11/2026, A-0108: «Pode ser de esconder o ano»).
export function anoDeAniversarioGenerico(iso: string | null | undefined): boolean {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(iso ?? ""));
  return !!m && (m[1] === "1900" || (m[1] === "1904" && m[2] === "02" && m[3] === "29"));
}

/** «20/05/1990», ou «20/05» quando o ano é o genérico. */
export function aniversarioParaMostrar(iso: string | null | undefined): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(iso ?? ""));
  if (!m) return "";
  return anoDeAniversarioGenerico(iso) ? `${m[3]}/${m[2]}` : `${m[3]}/${m[2]}/${m[1]}`;
}

export function orderDisplayCode(
  order: { dailyOrderNumber?: number | null; id: string },
  style?: string | null,
): string {
  if (style === "codigo" || order.dailyOrderNumber == null) {
    return String(order.id).slice(-4).toUpperCase();
  }
  return String(order.dailyOrderNumber).padStart(4, "0");
}

// Modalidade do pedido como a loja lê. «combinar» = loja sem entrega e sem retirada: o pedido não fixa nenhuma das
// duas (Pedro, 19/10/2026: «No máximo um "a combinar"»).
export function rotuloDaModalidade(modo: string | null | undefined): string {
  return modo === "entrega" ? "Entrega" : modo === "retirada" ? "Retirada" : modo === "combinar" ? "A combinar" : "—";
}

const DEV_HOST_FALLBACK = "vendanozap.app";

export function getPublicHost(): string {
  const host = window.location.host;
  if (host === "localhost" || host.includes("replit.dev") || host.includes("repl.co") || host.startsWith("127.")) {
    return DEV_HOST_FALLBACK;
  }
  return host;
}

// Helper to convert hex to HSL for dynamic catalog theming
export function hexToHSLString(hex: string): string {
  if (!hex) return "20 90% 55%"; // Default primary
  
  // Remove # if present
  hex = hex.replace(/^#/, '');
  
  // Parse hex to RGB
  let r = parseInt(hex.substring(0, 2), 16) / 255;
  let g = parseInt(hex.substring(2, 4), 16) / 255;
  let b = parseInt(hex.substring(4, 6), 16) / 255;

  let max = Math.max(r, g, b), min = Math.min(r, g, b);
  let h = 0, s = 0, l = (max + min) / 2;

  if (max !== min) {
    let d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case r: h = (g - b) / d + (g < b ? 6 : 0); break;
      case g: h = (b - r) / d + 2; break;
      case b: h = (r - g) / d + 4; break;
    }
    h /= 6;
  }

  return `${Math.round(h * 360)} ${Math.round(s * 100)}% ${Math.round(l * 100)}%`;
}

// Preço de produto por peso na unidade de EXIBIÇÃO: kg → "R$ X/kg"; gramas →
// padrão comercial "R$ X/100 g" (por 1 g sairia "R$ 0,04" e lê como erro); ml →
// "R$ X/100 ml" (T-0056, 04/11/2026). O canônico segue R$/kg — isto é só rótulo.
export function precoPeso(perKg: number, unidade: string | null | undefined): { valor: string; sufixo: string } {
  const r = precoDeReferencia(perKg, unidade);
  return { valor: formatCurrency(r.valor), sufixo: r.rotulo };
}

/** «g» ou «ml» quando o produto por peso se digita e mostra em unidades (1 ml = 1 g = 0,001 kg); null = kg. */
export function unidadeMenor(u: string | null | undefined): "g" | "ml" | null {
  return u === "g" || u === "ml" ? u : null;
}

// Porção de produto por peso: {kg, price|null}; número solto é o formato
// legado (vira price null). price preenchido é a palavra do lojista.
export type PorcaoPeso = { kg: number; price: number | null };
export function parsePorcoes(raw: unknown): PorcaoPeso[] {
  if (!Array.isArray(raw)) return [];
  return raw
    .map((v) => (typeof v === "number" ? { kg: v, price: null } : { kg: Number((v as any)?.kg), price: (v as any)?.price != null ? Number((v as any).price) : null }))
    .filter((o) => Number.isFinite(o.kg) && o.kg > 0);
}
export function pesoLabel(kg: number, unidade: string | null | undefined): string {
  // ml sempre em ml (perfume: «5 ml», nunca «0,005 kg»); kg e g como sempre.
  if (unidade === "ml") return `${Math.round(kg * 1000)} ml`;
  return unidade === "g" || kg < 1 ? `${Math.round(kg * 1000)} g` : `${String(kg).replace(".", ",")} kg`;
}

